import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AdminEventRow,
  CreateEventPayload,
  JudgeRoleId,
  UpdateEventPayload,
  UpsertJudgeProfilePayload,
} from "./client";
import { eventsApi } from "./client";

export type AdminEventListItem = AdminEventRow;

export const eventsPresentationKeys = {
  judgeSelectable: ["events", "judge-login-list"] as const,
  adminList: ["events", "admin-list"] as const,
  adminDetail: (eventId: string) =>
    ["events", "admin-detail", eventId] as const,
  adminJudgeProfiles: (eventId: string) =>
    ["events", eventId, "judge-profiles"] as const,
  judgeAccess: (eventId: string) =>
    ["events", eventId, "judge-access"] as const,
};

/** Events ในสถานะ scoring | closed — ให้เลือกก่อนเข้ากรรมการ */
export function useEventsQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: eventsPresentationKeys.judgeSelectable,
    queryFn: () => eventsApi.listPublicEvents(),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

export function useAdminEventsListQuery(enabled = true) {
  return useQuery({
    queryKey: eventsPresentationKeys.adminList,
    queryFn: () => eventsApi.listAdminEvents(),
    enabled,
    staleTime: 15_000,
  });
}

export function useAdminEventDetailQuery(eventId: string | undefined) {
  return useQuery({
    queryKey: eventsPresentationKeys.adminDetail(eventId ?? ""),
    queryFn: () => {
      if (!eventId) throw new Error("Missing event id");
      return eventsApi.getAdminEvent(eventId);
    },
    enabled: Boolean(eventId),
    staleTime: 15_000,
  });
}

/** ຊື່ງານ / metadata — ໃຊ້ດຽວກັບ admin detail query. */
export function useEventSummaryQuery(eventId: string | undefined) {
  return useAdminEventDetailQuery(eventId);
}

export function useAdminJudgeProfilesQuery(eventId: string | undefined) {
  return useQuery({
    queryKey: eventsPresentationKeys.adminJudgeProfiles(eventId ?? ""),
    queryFn: () => {
      if (!eventId) throw new Error("Missing event id");
      return eventsApi.listJudgeProfiles(eventId);
    },
    enabled: Boolean(eventId),
    staleTime: 15_000,
  });
}

export function usePatchJudgeProfileMutation(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      role,
      body,
    }: {
      role: JudgeRoleId;
      body: UpsertJudgeProfilePayload;
    }) => {
      if (!eventId) throw new Error("Missing event id");
      return eventsApi.patchJudgeProfile(eventId, role, body);
    },
    onSuccess: () => {
      toast.success("ບັນທຶກຂໍ້ມູນກຳມະການສໍາເລັດ");
      qc.invalidateQueries({
        queryKey: eventsPresentationKeys.judgeSelectable,
      });
      qc.invalidateQueries({ queryKey: eventsPresentationKeys.adminList });
      if (eventId) {
        qc.invalidateQueries({
          queryKey: eventsPresentationKeys.adminDetail(eventId),
        });
        qc.invalidateQueries({
          queryKey: eventsPresentationKeys.adminJudgeProfiles(eventId),
        });
      }
    },
    onError: () => toast.error("ບັນທຶກບໍ່ສຳເລັດ"),
  });
}

export function useCreateAdminEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEventPayload) => eventsApi.createAdminEvent(body),
    onSuccess: () => {
      toast.success("ສ້າງງານສໍາເລັດ");
      qc.invalidateQueries({ queryKey: eventsPresentationKeys.adminList });
    },
    onError: () => toast.error("ສ້າງງານບໍ່ສຳເລັດ"),
  });
}

export function useUpdateAdminEventMutation(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateEventPayload) => {
      if (!eventId) throw new Error("Missing event id");
      return eventsApi.updateAdminEvent(eventId, body);
    },
    onSuccess: () => {
      toast.success("ບັນທຶກສໍາເລັດ");
      qc.invalidateQueries({ queryKey: eventsPresentationKeys.adminList });
      if (eventId) {
        qc.invalidateQueries({
          queryKey: eventsPresentationKeys.adminDetail(eventId),
        });
      }
    },
    onError: () => toast.error("ບັນທຶກບໍ່ສຳເລັດ"),
  });
}

export function useDeleteAdminEventMutation(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!eventId) throw new Error("Missing event id");
      return eventsApi.deleteAdminEvent(eventId);
    },
    onSuccess: () => {
      toast.success("ລຶບງານສໍາເລັດ");
      qc.invalidateQueries({ queryKey: eventsPresentationKeys.adminList });
      if (eventId) {
        qc.removeQueries({
          queryKey: eventsPresentationKeys.adminDetail(eventId),
        });
      }
    },
    onError: () => toast.error("ລຶບງານບໍ່ສຳເລັດ"),
  });
}

export function useJudgeEventAccess(
  eventId: string | undefined,
  qEnabled = true,
) {
  return useQuery({
    queryKey: eventsPresentationKeys.judgeAccess(eventId ?? ""),
    queryFn: () => {
      if (!eventId) throw new Error("Missing event id");
      return eventsApi.getJudgeAccess(eventId);
    },
    enabled: Boolean(eventId) && qEnabled,
  });
}
